import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';

import { expVal } from '@confluence/feature-experiments';

import { isCanvasContentType } from './contentTypeConstants.ts';

/** This file identifies if certain actions should be rendered for the respective content type/mode
 * Each action has additional checks on top of what is listed here based on the permissions (via webItems), or by the parent (via allowedFeatures) at content-types-header/src/content-types-actions/MoreActions.tsx
 */

type ContentType = string | null | undefined;

export const getIsConvertRendered = (
	contentType: ContentType,
	contentMode?: string,
	isSpaceOverviewPage?: boolean,
): boolean => {
	if (contentType === 'page' && !isSpaceOverviewPage) {
		return contentMode === 'view';
	}
	return false;
};

export const getIsCopyRendered = (contentType: ContentType, contentMode?: string): boolean => {
	if (contentType === 'page') {
		return contentMode === 'live' || contentMode === 'view';
	}
	return isCanvasContentType(contentType) || contentType === 'database' || contentType === 'embed';
};

export const getIsArchiveRendered = (contentType: ContentType, contentMode?: string): boolean => {
	if (contentType === 'page') {
		return contentMode === 'live' || contentMode === 'view' || contentMode === 'edit';
	}
	return (
		isCanvasContentType(contentType) ||
		contentType === 'database' ||
		contentType === 'embed' ||
		contentType === 'folder'
	);
};

export const getIsExportRendered = (contentType: ContentType, contentMode?: string): boolean => {
	if (contentType === 'page') {
		return contentMode === 'live' || contentMode === 'view' || contentMode === 'archived';
	} else if (contentType === 'blogpost') {
		return contentMode === 'view';
	}
	return isCanvasContentType(contentType) || contentType === 'database';
};

export const getIsMoveRendered = (contentType: ContentType, contentMode?: string): boolean => {
	if (contentType === 'page') {
		return (
			contentMode === 'live' ||
			contentMode === 'view' ||
			contentMode === 'edit' ||
			contentMode === 'draft'
		);
	} else if (contentType === 'blogpost') {
		return contentMode === 'view' || contentMode === 'edit' || contentMode === 'draft';
	}
	return isCanvasContentType(contentType) || contentType === 'database' || contentType === 'embed';
};

export const getIsTemplatesRendered = (
	contentType: ContentType,
	contentMode?: string,
	readOnly?: boolean,
): boolean => {
	if (contentType === 'page') {
		// Templates are always available on live/draft pages. For published (edit) pages we only expose
		// templates when `platform_editor_blocks_conf` is enabled.
		return (
			contentMode === 'live' ||
			contentMode === 'draft' ||
			(contentMode === 'edit' && expVal('platform_editor_blocks_conf', 'isEnabled', false))
		);
	}
	if (isCanvasContentType(contentType)) {
		return !readOnly;
	}
	return contentType === 'database' && contentMode !== 'archived' && !readOnly;
};

export const getIsPresentRendered = (
	contentType: ContentType,
	contentMode?: string,
	isSpaceOverviewPage?: boolean,
): boolean => {
	return (
		(contentType === 'page' || contentType === 'blogpost') &&
		(contentMode === 'view' || contentMode === 'live') &&
		!isSpaceOverviewPage
	);
};

export const getIsSwitchToLiveEditRendered = (
	contentType: ContentType,
	contentMode?: string,
): boolean => {
	if (contentType === 'page') {
		return contentMode === 'edit' || contentMode === 'draft';
	}
	return false;
};

export const getIsViewChangesRendered = (
	contentType: ContentType,
	contentMode?: string,
): boolean => {
	if (contentType === 'page' || contentType === 'blogpost') {
		return contentMode === 'edit';
	}
	return false;
};

export const getIsUndoRedoFindRendered = (
	contentType: ContentType,
	contentMode?: string,
	hasEditPermission?: boolean,
	livePageMode?: string,
): boolean => {
	if (contentType === 'page' || contentType === 'blogpost') {
		const isLiveView = contentType === 'page' && contentMode === 'live' && livePageMode === 'view';

		// In live view the editor find dialog is the only way to search content inside
		// collapsed heading sections, which the editor hides with `display: none`. Readers
		// without edit permission get it too, because the dialog offers find without replace.
		// This rides the collapsible headings experiment, because collapsed content is the
		// reason live view needs the editor find at all.
		if (
			isLiveView &&
			UNSAFE_expValNoExposure('platform_editor_collapsible_headings', 'isEnabled', false)
		) {
			return true;
		}
		if (!hasEditPermission) {
			return false;
		}
		if (isLiveView) {
			return false;
		}
		return contentMode === 'live' || contentMode === 'edit' || contentMode === 'draft';
	}
	return false;
};

export const getIsRevertRendered = (contentType: ContentType, contentMode?: string): boolean => {
	if (contentType === 'page' || contentType === 'blogpost') {
		return contentMode === 'edit';
	}
	return false;
};

export const getIsRevertToLegacyEditorRendered = (
	contentType: ContentType,
	contentMode?: string,
	isDeleteDraftAllowed?: boolean,
	isAutoConvertedDraftRevertEnabled?: boolean,
): boolean => {
	if (contentType === 'page' || contentType === 'blogpost') {
		if (contentMode === 'edit' && isDeleteDraftAllowed) {
			return true;
		} else if (
			isAutoConvertedDraftRevertEnabled &&
			contentType === 'page' &&
			contentMode === 'draft'
		) {
			return true;
		} else if (contentMode === 'view') {
			return true;
		}
	}
	return false;
};

export const getIsAutomationsRendered = (
	contentType: ContentType,
	contentMode?: string,
	isSpaceOverviewPage?: boolean,
): boolean => {
	if (contentType === 'page' && !isSpaceOverviewPage) {
		return contentMode === 'view' || contentMode === 'live' || contentMode === 'archived';
	}
	return false;
};

export const getIsAdvancedRendered = (
	contentType: ContentType,
	contentMode: string | undefined,
): boolean => {
	return (
		(contentType === 'page' || contentType === 'blogpost') &&
		contentMode !== 'draft' &&
		contentMode !== 'edit'
	);
};

export const getIsAppsRendered = (contentType: ContentType, contentMode?: string): boolean => {
	if (contentType === 'page' || contentType === 'blogpost') {
		return contentMode === 'live' || contentMode === 'view';
	}
	return false;
};

export const getIsFeedbackRendered = (
	contentType: ContentType,
): contentType is 'whiteboard' | 'database' | 'slide' | 'embed' => {
	return isCanvasContentType(contentType) || contentType === 'database' || contentType === 'embed';
};

export const getIsHelpRendered = (
	contentType: ContentType,
): contentType is 'whiteboard' | 'database' | 'slide' => {
	return isCanvasContentType(contentType) || contentType === 'database';
};

export const getIsContentOwnershipRendered = (contentType: ContentType): boolean => {
	return contentType === 'embed';
};

export const getIsWatchRendered = (contentType: ContentType, contentMode?: string): boolean => {
	if (contentType === 'page' || contentType === 'blogpost') {
		return contentMode !== 'draft';
	}

	return isCanvasContentType(contentType) || contentType === 'database' || contentType === 'folder';
};

export const getIsPreferencesRendered = (
	contentType: ContentType,
): contentType is 'whiteboard' | 'slide' => {
	return isCanvasContentType(contentType);
};

export const getIsConvertToPageRendered = (
	contentType: ContentType,
	contentMode?: string,
): boolean => {
	return contentType === 'page' && contentMode === 'live';
};

export const getIsPreviewRendered = (contentType: ContentType, contentMode?: string): boolean => {
	return (
		(contentType === 'page' || contentType === 'blogpost') &&
		(contentMode === 'edit' || contentMode === 'draft')
	);
};

export const getIsPageHistoryRendered = (
	contentType: ContentType,
	contentMode?: string,
): boolean => {
	if (contentType === 'page') {
		return contentMode === 'live' || contentMode === 'view' || contentMode === 'archived';
	} else if (contentType === 'blogpost') {
		return contentMode === 'view';
	}
	return false;
};
